import { useEffect, useState } from "react";
import Image from "next/image";
import { useAccount, useContractWrite, useSwitchNetwork } from "wagmi";
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  Button as CButton,
  Spinner,
  useColorMode,
} from "@chakra-ui/react";
import { parseEther } from "ethers/lib/utils";

// Components
import Alert from "components/Alert";
import Button from "./CustomButtom";

// Config
import { config } from "config/network";

// Constants
import { TXN_TYPES } from "constants/transactions";
import { VaultType } from "constants/vaults";
import {
  DEPOSIT_CTA,
  DEPOSIT_MAX_CTA,
  ENTER_DEPOSIT_AMOUNT,
} from "constants/analytics";

// Hooks
import useApp from "hooks/useApp";
import useChain from "hooks/useChain";
import useTokenBalance from "hooks/useTokenBalance";

// Utils
import { getMessage, isNumber, toFixedWithoutRounding } from "utils/common";

const WALLET_CAP = 1000;

const Deposit = ({
  vault,
  disabledTxnMessage,
}: {
  vault: VaultType;
  disabledTxnMessage: string;
}) => {
  const { status, switchChain } = useChain();
  const { switchNetwork } = useSwitchNetwork();
  const { isConnected, address } = useAccount();

  const { token } = useTokenBalance();

  const { colorMode } = useColorMode();

  const {
    openWalletModal,
    updateTransactionDetails,
    setTxnError,
    initiateNewTxn,
    track,
  } = useApp();

  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const { isLoading: isDepositing, write: deposit } = useContractWrite({
    mode: "recklesslyUnprepared",
    address: vault.address,
    abi: vault.abi,
    functionName: "deposit",
    overrides: {
      value: parseEther(
        toFixedWithoutRounding(
          amount && !Number.isNaN(+amount) ? amount : "0",
          8
        )
      ),
    },
    onError(error: any) {
      setTxnError(getMessage(error));
    },
    onSuccess(data) {
      updateTransactionDetails(
        data.hash,
        TXN_TYPES.DEPOSIT,
        amount,
        config.nativeCurrency.symbol
      );
    },
  });

  useEffect(() => {
    if (amount) {
      const error = checkInput(amount);
      setError(error);
    }
  }, [token.balance, address]);

  useEffect(() => {
    if (amount && !isConnected) {
      setAmount("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, address]);

  const checkInput = (value = amount.trim()) => {
    if (!isConnected) return "";

    let error = "";

    if (!+value || Number.isNaN(+value)) {
      error = "This field can't be blank or zero. Please provide valid input.";
    } else if (Number(value) > token.balance) {
      error = "Insufficient funds to execute the transaction.";
    } else if (Number(value) > WALLET_CAP) {
      error = `This is a capped launch, can't deposit more than ${WALLET_CAP} BNB`;
    }

    return error;
  };

  const handleChange = (value: string) => {
    const val = value.trim();

    const error = checkInput(val);

    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= 8) {
          setAmount(val === "." ? "0." : val);
        }
      }
    }

    setError(error);
  };

  const handleMaxClick = () => {
    // const val = token.balance - stakingFees;
    const val = token.balance;

    track(DEPOSIT_MAX_CTA, { deposit_amount_entered: token.balance });

    handleChange(toFixedWithoutRounding(val.toString(), 8).toString());
  };

  const handleDeposit = async (e: any) => {
    e.stopPropagation();

    const error = checkInput();
    if (error) {
      setError(error);
    } else {
      track(DEPOSIT_CTA, {
        deposit_amount_entered: amount,
      });
      initiateNewTxn(false);
      deposit?.();
      setAmount("");
    }
  };

  return (
    <Box mt="1rem">
      <Text fontSize={{ base: 12, lg: 16 }}>
        My BNB in Wallet :{" "}
        <Text as="span" fontFamily="semi-bold">
          {token.balance}
        </Text>
      </Text>
      <InputGroup size="lg" mt="1.5rem" mb="1rem">
        <InputLeftElement pointerEvents="none">
          <Image
            src={token.icon[colorMode]}
            width={28}
            height={28}
            alt="token-icon"
          />
        </InputLeftElement>
        <Input
          type="text"
          borderWidth="2px"
          borderRadius="0.5rem"
          isInvalid={!!error}
          borderColor={error ? "crimson" : "brand.primary"}
          placeholder={`Enter ${token.symbol} Amount `}
          value={amount}
          fontSize={{ base: 14, lg: 16 }}
          onChange={(e) => {
            track(ENTER_DEPOSIT_AMOUNT, {
              amount: e.target.value,
            });
            handleChange(e.target.value);
          }}
          _focus={{
            boxShadow: "none",
            outline: "none",
            borderColor: error ? "crimson" : "brand.primary",
            borderWidth: "2px",
          }}
          _hover={{
            borderColor: error ? "crimson" : "brand.primary",
            borderWidth: "2px",
            _disabled: {
              borderWidth: "1px",
            },
          }}
        />
        <InputRightElement width="4.5rem">
          <CButton
            bg="brand.backgroundLight"
            h="1.75rem"
            size="sm"
            p={1}
            onClick={handleMaxClick}
            _dark={{
              color: "brand.textColor",
            }}
            _hover={{
              bg: "brand.primary",
              color: "white",
              outline: "none",
              borderColor: "brand.primary",
            }}
            _focus={{
              outline: "none",
            }}
          >
            Max
          </CButton>
        </InputRightElement>
      </InputGroup>
      {!error && (
        <Alert
          type="info"
          withIcon={true}
          display={isConnected ? "none" : "flex"}
        >
          <Text
            fontSize={{ base: 12, lg: 14 }}
            color="#000000"
            _dark={{ color: "#ffffff" }}
          >
            Connect your wallet to start depositing.
          </Text>
        </Alert>
      )}
      {error && (
        <Alert type="danger" withIcon={true}>
          <Text
            fontSize={{ base: 12, lg: 14 }}
            color="#000000"
            _dark={{ color: "#ffffff" }}
          >
            {error}
          </Text>
        </Alert>
      )}
      {!!disabledTxnMessage && (
        <Alert type="danger" withIcon={true}>
          <Text
            fontSize={{ base: 12, lg: 14 }}
            color="#000000"
            _dark={{ color: "#ffffff" }}
          >
            {disabledTxnMessage}
          </Text>
        </Alert>
      )}
      {isConnected && vault.isPaused && (
        <Alert type="warning" withIcon={true}>
          <Text
            fontSize={{ base: 10, lg: 14 }}
            color="#000000"
            _dark={{ color: "#ffffff" }}
          >
            Deposit feature in temporarily unavailable, we will be back soon.
            Thank you for your patience.
          </Text>
        </Alert>
      )}
      <Button
        mt="2rem"
        gap={0}
        onClick={
          error || !!disabledTxnMessage
            ? undefined
            : isConnected
            ? vault.isPaused
              ? undefined
              : !status
              ? !switchNetwork
                ? undefined
                : switchChain
              : handleDeposit
            : () => {
                openWalletModal({
                  wallet_cta: "portfolio_deposit_tab",
                  vault: vault.name,
                });
              }
        }
        disabled={
          isConnected
            ? !vault.isPaused
              ? !!disabledTxnMessage || isDepositing || !!error
              : true
            : false
        }
        size={{ base: "sm", lg: "lg" }}
      >
        {isDepositing ? (
          <Spinner size="md" color="brand.textLight" />
        ) : (
          <Text>
            {isConnected
              ? !status
                ? !switchNetwork
                  ? "Switch Network in the Wallet"
                  : "Switch Network"
                : "Deposit"
              : "Connect wallet"}
          </Text>
        )}
      </Button>
    </Box>
  );
};

export default Deposit;
